import React, { useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import {
  colors2,
  getVal,
  TooltipV2,
  UI_BODY_BOLD_SM_DF,
  UI_CAPTION_SEMIBOLD,
  UI_TAG,
} from "oolib";
import { ButtonTertiaryCompact } from "oolib/dist/v2/components/Buttons";
import { useGetQueryData } from "../../../../../../../utils/react-query-hooks/general";
import { WidgetErrorState, WidgetLoadingState } from '../../WidgetRenderer/comps/altStatePlaceholders';
import { useGetAnnotationsLazy, useGetDataQuery, useES_CountData } from '../../../../../../../utils/react-query-hooks/getData';
import { getTenant } from '@/TENANT_CONFIGS';
import { ConfigForm } from './ConfigForm';
import { Summary, useSummary, SummaryV2, useAnnoSummaryV2 } from '@/components/analysisModules/AnnotationExplorer/comps/Summary';
import { getAnnoExplorerConfigsForThisTenant } from '@/components/analysisModules/AnnotationExplorer/utils/getAnnoExplorerConfigsForThisTenant';

import { MasonryCardGridShell } from '@/components/analysisModules/AnnotationExplorer/comps/MasonryCardGridShell';
import { useElemInView } from '@/utils/customHooks/useElemInView';
import LexicalTextEditor from '@/components/inputs/TextEditor/LexicalTextEditor';
import { getTplsWithAnnosEnabled } from '@/components/analysisModules/AnnotationExplorer/useGetAnnotationExplorerData';
import { useUpdateLoadedWidgetsTracker } from '../../../ReportLandingContext/useUpdateLoadedWidgetsTracker';
import { useReportLandingContext } from '../../../ReportLandingContext';
import { StyledCompWrapper } from '../../../styled';
import { AnnoCard } from './comps/AnnoCard';
import { FadeGradientWhenScroll } from '@/components/dataviz/common/FadeGradientWhenScroll';
import OverlayScrollbar from '@/components/UI/OverlayScrollbar';

export const Component = ({
  id,
  widgetIdx,
  activeFilters = [],
  presetActiveFilters = [],
  style = {},

  selectedContentTypes = [],
  displaySettings = [],
  cardConfigs = {},
  columnSpan,
  // Public-share replay: a frozen { pages: [{ data:[...annos] }] } blob. When present the
  // widget renders these annotations instead of fetching (no live query => no infinite
  // scroll). See SHARED_REPORTS_DESIGN.md.
  prefetchedData,
  // True only on the public report page — hides the AI-summary control.
  isPublicReport = false,
}) => {
  const allTpls = useGetQueryData(["allTpls"]);
  const { updateWidgetDataStore, reportId } = useReportLandingContext() || {};
  
  // const { ai = { annotationExplorer: {} } } = useGetDataQuery("platformConfigs") || {};
  // const annotationExplorerAIConfigs = ai?.annotationExplorer || {};

  const tplOptions = useMemo(() => {
    const options = getTplsWithAnnosEnabled({ allTpls });
    return options;
  }, [allTpls]);



  const totalFilters = [...presetActiveFilters, ...activeFilters, ...displaySettings];

  // AI summary gating — mirror the AnnotationExplorer page so tenant/prod flags are respected.
  const annoExplorerConfigs = getAnnoExplorerConfigsForThisTenant();
  const disableSummarizeButton = annoExplorerConfigs?.disableSummarizeButton;
  const { FEATURES_DISABLED_ON_PROD: disabledFeatures = [] } = useGetQueryData(["platformConfigs"]) || {};
  const isProd = import.meta.env.VITE_ENV === "prod";
  const useV2Summary = !(isProd && disabledFeatures.includes("annoSummaryV2"));

  // selectedContentTypes are {display, value} objects (DropdownMulti w/o saveValueAsString).
  // The summary endpoint's `contentTypes` field is applied directly as $in on meta.contentType,
  // so it needs plain value strings — same mapping the AnnotationExplorer page does.
  const contentTypeValues = useMemo(
    () => (selectedContentTypes || []).map((o) => o?.value ?? o).filter(Boolean),
    [selectedContentTypes]
  );

  const {
    summary: summaryV2,
    shortSummary: summaryV2Short,
    empty: summaryEmpty,
    hasTriggered: summaryHasTriggered,
    loading: summaryLoading,
    isRefreshing: summaryRefreshing,
    generate: generateSummary,
    refresh: refreshSummary,
  } = useAnnoSummaryV2({
    activeFilters: totalFilters,
    contentTypes: contentTypeValues,
    enabled: useV2Summary && !disableSummarizeButton && !isPublicReport,
  });

  // The widget's full annotation scope — content-type filter + all widget filters.
  // Shared by the data fetch, the V1 count query, and the V1 summary so all three
  // see the same set of annotations.
  const annoActiveFilters = [
    {
      filterId: "contentTypeFilter_contentType",
      values: selectedContentTypes,
      target: {
        filterType: "valuePathType",
        valuePath: "meta.contentType",
      },
    },
    ...totalFilters,
  ];

  // V1 fallback — mirrors ListingModuleAnnotationExplorer: when V2 is prod-gated
  // (annoSummaryV2 in FEATURES_DISABLED_ON_PROD), show the old button-gated
  // summary instead of nothing.
  const showOldSummary = !useV2Summary && !disableSummarizeButton && !isPublicReport && selectedContentTypes.length >= 1;

  // V1's limit check needs the scope's total annotation count (same source as the
  // AnnotationExplorer page). Only fetched when the fallback is actually shown.
  const { data: countData } = useES_CountData({
    contentTypes: ["annotations"],
    enabled: showOldSummary,
    activeFilters: annoActiveFilters,
  });

  const {
    summary: summaryOld,
    generateSummaryStatus,
    handleOnClickGenerateSummary,
    buttonDisabled: summarizeButtonDisabled,
    buttonText: summarizeButtonText,
    tooltipText: summarizeTooltipText,
  } = useSummary({
    activeFilters: annoActiveFilters,
    dataSource: "annotations",
    totalCount: countData?.count,
    searchTerm: "",
  });

  const {
    data: dataFromApiCall,
    fetchNextPage: fetchNextPageFromApiCall,
    hasNextPage: hasNextPageFromApiCall,
    status: statusFromApiCall,
    error,
    isFetchingNextPage,
    colorBreakdownStats, } = useGetAnnotationsLazy({
    activeFilters: annoActiveFilters,
    // activeSort: {"annotations.fragment.allText" : 1}, mapping issue
    dataSource: "annotations",
    translationConfig: {
      customBlocks: [
        { comp: "TitleInput", props: {}, valuePath: "main.title" },
        {
          comp: "LexicalTextEditor",
          props: {},
          valuePath: "annotations.fragment",
        },
      ],
    },
    queryOptions: { enabled: !prefetchedData },
  });

  // On replay, render the frozen snapshot; live fetch + infinite scroll are disabled.
  const data = prefetchedData || dataFromApiCall;
  const status = prefetchedData ? "success" : statusFromApiCall;
  const hasNextPage = prefetchedData ? false : hasNextPageFromApiCall;
  const fetchNextPage = prefetchedData ? () => {} : fetchNextPageFromApiCall;

  useUpdateLoadedWidgetsTracker({status, widgetIdx})

  const { observerRef } = useElemInView({
    callback: fetchNextPage,
    enabled: !!hasNextPage && !isFetchingNextPage,
  });

  const processedData = useMemo(() => {
    if (status === "success" && data?.pages) {
      const flattenedData = data.pages.flatMap(page => page.data || []);
      // Drop annotations with no text content — LexicalTextEditor crashes on an
      // empty editor state ("setEditorState: the editor state is empty").
      return flattenedData.filter(d => d?.annotations?.fragment?.allText?.trim());
    }
    return [];
  }, [ status, data ]);

  // Capture: push rendered annotations to the central store so a share snapshot can replay
  // them. The public-snapshot size cap is applied by the snapshot builder, not here — this
  // widget stays agnostic to public-report concerns. No-op on replay.
  useEffect(() => {
    if (prefetchedData || status !== "success" || !processedData.length) return;
    updateWidgetDataStore && updateWidgetDataStore(id, { pages: [{ data: processedData }] });
  }, [prefetchedData, status, processedData, id, updateWidgetDataStore]);

  const renderCard = (cols, colI, singleAnno, i, ary, isLastCard) => {
    return (
      <AnnoCard
        key={singleAnno._id + singleAnno.annoKey}
        singleAnno={singleAnno}
        cardConfigs={cardConfigs}
        widgetName="Annotations"
        reportId={reportId}
        widgetId={id}
        widgetIdx={widgetIdx}
        lastCardObserverRef={
          (isLastCard && hasNextPage)
             ? observerRef
             : null
         }
      />
    );
  };

  // show number of cards in widget based on how much column span it occupies
  const numberOfCards = columnSpan >= 9 ? 4 : columnSpan >= 6 ? 2 : columnSpan <= 4 ? 1 : 4;
  
  return (
    <>
    {status === "loading" ? (
      <WidgetLoadingState/>
    ) : status === "error" ? (
      <WidgetErrorState error={error}/>
    ) : (
          <div style={{ maxHeight: '500px', position: 'relative', display: "flex", padding: "1.6rem", paddingBottom: 0 }}>
          <OverlayScrollbar style={{ flex: 1 }} overlay={false}>

              {useV2Summary && !disableSummarizeButton && !isPublicReport && selectedContentTypes.length >= 1 && (
                <div style={{ marginBottom: "1.6rem" }}>
                  <SummaryV2
                    summary={summaryV2}
                    shortSummary={summaryV2Short}
                    empty={summaryEmpty}
                    hasTriggered={summaryHasTriggered}
                    loading={summaryLoading}
                    isRefreshing={summaryRefreshing}
                    onGenerate={generateSummary}
                    onRefresh={refreshSummary}
                    style={{ padding: 0 }}
                  />
                </div>
              )}
              {/* Old "Summarize" summary — only shown when V2 is disabled (prod fallback),
                  same behavior as ListingModuleAnnotationExplorer. */}
              {showOldSummary && (
                <div style={{ marginBottom: "1.6rem" }}>
                  {summaryOld ? (
                    <Summary
                      summary={summaryOld}
                      onRefresh={handleOnClickGenerateSummary}
                      isRefreshing={generateSummaryStatus === "loading"}
                    />
                  ) : summarizeTooltipText ? (
                    <TooltipV2 text={summarizeTooltipText} placement="bottom">
                      <ButtonTertiaryCompact
                        icon="Sparkle"
                        onClick={handleOnClickGenerateSummary}
                        disabled={summarizeButtonDisabled}
                      >
                        {summarizeButtonText}
                      </ButtonTertiaryCompact>
                    </TooltipV2>
                  ) : (
                    <ButtonTertiaryCompact
                      icon="Sparkle"
                      onClick={handleOnClickGenerateSummary}
                      disabled={summarizeButtonDisabled}
                    >
                      {summarizeButtonText}
                    </ButtonTertiaryCompact>
                  )}
                </div>
              )}
                <MasonryCardGridShell
                  dataFetchStatus={status}
                  dataFetchError={error}
                  isFetchingNextPage={isFetchingNextPage}
                  data={processedData}
                  renderCard={renderCard}
                  forceCol={numberOfCards}
                  style={{ gap: "0.8rem" }}
                />
              <FadeGradientWhenScroll />
          </OverlayScrollbar>
          </div>
        )}
    </>
  );
};

export const optionDef = { 
  display: "Annotation Cards", 
  icon: "File", 
  value: "annotationExplorer", 
  comp: 'WidgetAnnotationExplorer' 
};

export const defaultConfig = {
  props: { 
    selectedContentTypes: [],
    displaySettings: [],
    cardConfigs: {}
  }
};

export const processRawConfigFn = (rawConfig) => { return rawConfig };

export const someReqPropsUnset = (config) =>
  !config?.props?.selectedContentTypes?.length;

export { ConfigForm }; 